import {
  SiteReportDraft,
  type SiteReportSnapshot,
  type SiteWorkflowState,
  siteQuantityUnits,
} from '@epc/contracts';

/** Статус наряда-допуска по работе плана дня (КЧ-05). */
export interface DayPlanLinePermit {
  required: boolean;
  ok: boolean;
  kind: string | null;
  permit: { id: string; number: string; kind: string; validUntil: string } | null;
  requires: string[];
  reason: string | null;
}

export interface DayPackage {
  packageId: string;
  name: string;
  scheduleId: string;
  scheduleRevision: number;
  planSnapshotId: string;
  sourceRevisions: Array<{ type: string; id: string; revision: number }>;
  lines: Array<{
    packageItemId: string;
    activityId: string;
    name: string;
    unit: string | null;
    totalQuantity: string | null;
    actualQuantity: string | null;
    /** Объём на день из сменного задания графика; null — не задан */
    plannedQuantity: string | null;
    plannedQuantityReason?: 'shift-task' | 'day-quantity-not-allocated';
    permit?: DayPlanLinePermit;
  }>;
}
export interface DayPlan {
  projectId: string;
  date: string;
  projectActive: boolean;
  packages: DayPackage[];
}
export const SITE_STATES: Record<SiteWorkflowState, string> = {
  draft: 'Черновик',
  submitted: 'На проверке',
  verified: 'Проверен ПТО',
  accepted: 'Принят',
  returned: 'Возвращён на исправление',
  rejected: 'Отклонён',
};

export function draftFromPackage(
  projectId: string,
  day: string,
  pkg: DayPackage,
  timeZone: string,
  shift: string,
): SiteReportDraft {
  return SiteReportDraft.parse({
    schemaVersion: 1,
    projectId,
    objectId: null,
    packageId: pkg.packageId,
    reportDay: day,
    projectTimeZone: timeZone,
    shiftKey: shift,
    planSnapshotId: pkg.planSnapshotId,
    scheduleRevision: pkg.scheduleRevision,
    formVersion: 1,
    lines: [],
    photos: [],
    note: '',
    weather: null,
  });
}

export function editableReport(report: SiteReportSnapshot | null, userId: string) {
  return !report || (report.authorId === userId && ['draft', 'returned'].includes(report.state));
}

/** Обновляет только основание плана; stable line identity и введённые значения сохраняются. */
export function refreshDraftPlan(draft: SiteReportDraft, pkg: DayPackage): SiteReportDraft {
  if (draft.packageId !== pkg.packageId) throw new Error('Выбран другой пакет работ');
  for (const line of draft.lines) {
    if (
      !pkg.lines.some(
        (p) =>
          p.packageItemId === line.packageItemId && p.activityId === line.activityId && p.unit === line.unit,
      )
    )
      throw new Error('Состав работ изменился. Сохранённый ввод оставлен для сверки с новым планом.');
  }
  // План на день в строках — из нового плана (сменное задание могли выдать или поменять)
  const planned = new Map(pkg.lines.map((p) => [p.packageItemId, p.plannedQuantity]));
  return {
    ...draft,
    planSnapshotId: pkg.planSnapshotId,
    scheduleRevision: pkg.scheduleRevision,
    lines: draft.lines.map((l) => ({ ...l, plannedQuantity: planned.get(l.packageItemId) ?? null })),
  };
}

/** Удаляет выбранную работу и только её фото-ссылки из локального черновика. */
export function removeDraftLine(draft: SiteReportDraft, lineId: string): SiteReportDraft {
  return {
    ...draft,
    lines: draft.lines.filter((line) => line.id !== lineId),
    photos: draft.photos.filter((photo) => photo.lineId !== lineId),
  };
}

/** Семя прошлой смены: люди и техника без объёмов, фото и материалов (ПР-19). */
export type PreviousReportSeed = {
  reportId: string;
  reportDay: string;
  shiftKey: string;
  lines: Array<{
    activityId: string;
    packageItemId: string;
    labor: Array<{
      workerId: string | null;
      profession: string;
      headcount: number;
      hours: string;
      idleHours: string;
      idleReason: string | null;
    }>;
    machines: Array<{
      machineId: string;
      hours: string;
      idleHours: string;
      idleReason: string | null;
    }>;
  }>;
};

/**
 * Подставляет в черновик работы из пакета с людьми и техникой прошлой смены.
 * Объёмы, фото и материалы не копируются — их вводят заново.
 */
export function applyPreviousReportSeed(
  draft: SiteReportDraft,
  pkg: DayPackage,
  seed: PreviousReportSeed,
): SiteReportDraft {
  const byItem = new Map(seed.lines.map((l) => [l.packageItemId, l]));
  const lines: SiteReportDraft['lines'] = [];
  for (const p of pkg.lines) {
    if (!p.unit) continue;
    const prev = byItem.get(p.packageItemId);
    lines.push({
      id: crypto.randomUUID(),
      activityId: p.activityId,
      packageItemId: p.packageItemId,
      unit: p.unit,
      plannedQuantity: p.plannedQuantity,
      claimedQuantity: '',
      ruleRef: null,
      stepIds: [],
      labor: (prev?.labor ?? []).map((x) => ({
        id: crypto.randomUUID(),
        workerId: x.workerId,
        profession: x.profession,
        headcount: x.headcount,
        hours: x.hours,
        idleHours: x.idleHours,
        idleReason: x.idleReason,
      })),
      machines: (prev?.machines ?? []).map((x) => ({
        id: crypto.randomUUID(),
        machineId: x.machineId,
        hours: x.hours,
        idleHours: x.idleHours,
        idleReason: x.idleReason,
        telemetryRef: null,
      })),
      materials: [],
      note: '',
    });
  }
  return { ...draft, lines, photos: [] };
}

/**
 * Отклонение факта смены от плана на день (ПР-02): «+1,5 м3 к плану (112 %)» или «−2 м3 до плана (84 %)».
 * null — плана нет или факт ещё не введён (или введён с ошибкой).
 */
export function dayDeviation(
  planned: string | null,
  claimed: string,
  unit: string,
): { text: string; over: boolean | null } | null {
  if (!planned || !claimed) return null;
  let p: bigint;
  let c: bigint;
  try {
    p = siteQuantityUnits(planned);
    c = siteQuantityUnits(claimed);
  } catch {
    return null;
  }
  const diff = c - p;
  const abs = diff < 0n ? -diff : diff;
  const whole = abs / 1_000_000n;
  const frac = (abs % 1_000_000n).toString().padStart(6, '0').replace(/0+$/, '');
  const amount = `${whole}${frac ? `,${frac}` : ''} ${unit}`;
  // Процент с округлением до десятых: (c·1000 + p/2) / p
  const pct = p > 0n ? ` (${String(Number((c * 1000n + p / 2n) / p) / 10).replace('.', ',')} %)` : '';
  if (diff === 0n) return { text: `По плану${pct}`, over: null };
  return diff > 0n
    ? { text: `+${amount} к плану${pct}`, over: true }
    : { text: `−${amount} до плана${pct}`, over: false };
}
